import { v4 as uuidv4 } from 'uuid'

import type { CreateChatData } from './chatQueryTypes'
import type { IChat } from '../types/chat'

/**
 * Prefix of the local-only chat id used while POST `/chats` is in flight. It keys
 * the placeholder's thinking / responding / stream state so the ChatLoading page
 * renders exactly like the real chat page, and is never sent to the API.
 *
 * TODO: once the API accepts a client-generated chat id on POST `/chats`, mint a
 * plain UUID here, send it as `chat_id` in the payload and navigate straight to
 * `Chat` with it. That removes the ChatLoading route, the placeholder store state
 * and the second navigation on `chat_created` — the chat page would render the
 * optimistic chat under its final id from the first paint.
 */
export const PLACEHOLDER_CHAT_ID_PREFIX = 'placeholder:'

/** The title a chat has, here and on the API, until one is generated for it. */
export const DEFAULT_CHAT_TITLE = 'New Chat'

/**
 * Optimistic chat shown by the ChatLoading page: the message the user just sent,
 * with no assistant row yet — the same state the real chat page is in between
 * `chat_created` and the first `message_chunk`, so the swap is seamless.
 */
export function buildPlaceholderChat(data: CreateChatData): IChat {
  const timestamp = Math.floor(Date.now() / 1000)
  return {
    id: `${PLACEHOLDER_CHAT_ID_PREFIX}${uuidv4()}`,
    title: DEFAULT_CHAT_TITLE,
    gpt_id: data.gpt_id ?? '',
    owner_id: '',
    is_preview: false,
    created_at: timestamp,
    updated_at: timestamp,
    messages: [
      {
        // `Chat.vue` keys each message group on the first message's id; without
        // one the group renders under an undefined key.
        id: uuidv4(),
        role: 'user',
        content: data.content,
        model: data.model ?? '',
        timestamp,
        files: data.files ?? [],
        // Drives the image placeholder under the bubble from the first paint,
        // rather than only once the assistant row exists.
        doGenerateImage: data.generate_image,
      },
    ],
  }
}
